import {
  Criteria,
  PaginatedAPIResponse,
  SalesOrder,
  SelectionFilter,
  SoftwareTableRow,
  SoftwareTitleForAssignment,
  SortDirection,
} from '@zspace/types';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Element } from 'react-bulma-components';
import useHttpRequest from '../../shared/hooks/http-request';
import SoftwareInbox from '../../software/software-inbox/software-inbox';
import DefaultInboxTable from '../../ui/inbox-table/default-inbox-table/default-inbox-table';
import InboxTable from '../../ui/inbox-table/inbox-table';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import SelectionIndicator from '../../ui/selection-indicator/selection-indicator';
import { OnTableChangeData } from '../../ui/table/types';
import SoftwareAssignmentTable from '../software-assignment-table/software-assignment-table';

const FETCH_SOFTWARE_SALES_ORDERS_ERROR_MESSAGE =
  'The software could not be fetched. Please try again';

const initialCriteria: Criteria = {
  itemsPerPage: 10,
  pageNumber: 1,
  search: '',
  sortBy: 'title',
  sortDirection: SortDirection.ASC,
};

export type SoftwareAssignmentInboxTableProps = {
  salesOrders: SalesOrder[];
  fetchMoreSalesOrders: () => void;
  hasMoreSalesOrders: boolean;
  fetchSoftwareBySalesOrderRequest: (
    salesOrderId: string,
    criteria: Criteria
  ) => Promise<PaginatedAPIResponse<SoftwareTableRow>>;
  loadingInitialData: boolean;
  onFilter: (value: Criteria) => void;
  tableEmptyText: string;
  selectedSoftwareBySalesOrder: Map<string, SoftwareTitleForAssignment[]>;
  onSoftwareTitleClick: (
    salesOrderId: string,
    software: SoftwareTitleForAssignment[],
    add: boolean
  ) => void;
};

export function SoftwareAssignmentInboxTable({
  salesOrders,
  fetchMoreSalesOrders,
  hasMoreSalesOrders,
  fetchSoftwareBySalesOrderRequest,
  loadingInitialData,
  onFilter,
  tableEmptyText,
  selectedSoftwareBySalesOrder,
  onSoftwareTitleClick,
}: SoftwareAssignmentInboxTableProps) {
  const latestDataFetchRef = useRef(0);
  const [selectedSalesOrderId, setSelectedSalesOrderId] = useState<string>();
  const [software, setSoftware] = useState<SoftwareTableRow[]>([]);
  const [softwareBySalesOrderResponse, setSoftwareBySalesOrderResponse] =
    useState<PaginatedAPIResponse<SoftwareTableRow>>();
  const [criteriaFilter, setCriteriaFilter] = useState<Criteria>({
    ...initialCriteria,
  });

  const { executeHttpRequest } = useHttpRequest();
  const [loadingInitialSoftware, setLoadingInitialSoftware] = useState(false);

  const tableConfig = useMemo(() => {
    return {
      ...criteriaFilter,
      pages: softwareBySalesOrderResponse?.pages ?? 1,
    };
  }, [criteriaFilter, softwareBySalesOrderResponse?.pages]);

  const selectedSoftware = useMemo(() => {
    if (!selectedSoftwareBySalesOrder) return [];
    return Array.from(selectedSoftwareBySalesOrder.values()).flat();
  }, [selectedSoftwareBySalesOrder]);

  const activeSalesOrderSelectedSoftwareCount = useMemo(
    () =>
      selectedSoftwareBySalesOrder?.get(selectedSalesOrderId ?? '')?.length ??
      0,
    [selectedSalesOrderId, selectedSoftwareBySalesOrder]
  );

  const activeSalesOrderTotalSoftwareCount = useMemo(
    () => softwareBySalesOrderResponse?.total ?? 0,
    [softwareBySalesOrderResponse?.total]
  );

  const fetchSoftwareBySalesOrder = useCallback(
    (salesOrderId: string, criteria: Criteria, clearData?: boolean) => {
      const currentRef = ++latestDataFetchRef.current;

      return executeHttpRequest({
        asyncFunction: async () => {
          const response = await fetchSoftwareBySalesOrderRequest(
            salesOrderId,
            criteria
          );

          if (currentRef !== latestDataFetchRef.current) return;

          if (clearData) {
            setSoftware(response.data);
          } else {
            setSoftware((prevSoftware) => [...prevSoftware, ...response.data]);
          }
          setSoftwareBySalesOrderResponse(response);
        },
        customErrorMessage: FETCH_SOFTWARE_SALES_ORDERS_ERROR_MESSAGE,
      });
    },
    [executeHttpRequest, fetchSoftwareBySalesOrderRequest]
  );

  const onSalesOrderSelect = useCallback(
    async (salesOrderId: string) => {
      const updatedCriteria = {
        ...initialCriteria,
        search: criteriaFilter.search,
      };
      setCriteriaFilter(updatedCriteria);
      setSelectedSalesOrderId(salesOrderId);

      setLoadingInitialSoftware(true);
      await fetchSoftwareBySalesOrder(salesOrderId, updatedCriteria, true);
      setLoadingInitialSoftware(false);
    },
    [criteriaFilter.search, fetchSoftwareBySalesOrder]
  );

  const onTableChange = useCallback(
    async (value: OnTableChangeData<SoftwareTableRow>) => {
      const updatedCriteria = {
        ...criteriaFilter,
        ...value.config,
        sortBy: value.column?.key ?? criteriaFilter.sortBy,
      };
      setCriteriaFilter(updatedCriteria);

      setLoadingInitialSoftware(true);
      await fetchSoftwareBySalesOrder(
        selectedSalesOrderId!,
        {
          ...criteriaFilter,
          ...updatedCriteria,
        },
        true
      );
      setLoadingInitialSoftware(false);
    },
    [criteriaFilter, fetchSoftwareBySalesOrder, selectedSalesOrderId]
  );

  const onSearchChange = useCallback(
    async (value: string) => {
      const updatedCriteria = {
        ...initialCriteria,
        search: value,
      };
      setCriteriaFilter(updatedCriteria);
      setSoftware([]);
      setSelectedSalesOrderId(undefined);
      onFilter(updatedCriteria);
    },
    [onFilter]
  );

  const inbox = useMemo(
    () => (
      <SoftwareInbox
        fetchMore={fetchMoreSalesOrders}
        hasMore={hasMoreSalesOrders}
        onClick={onSalesOrderSelect}
        salesOrders={salesOrders}
        selectedSalesOrderId={selectedSalesOrderId}
        loadingInitialData={loadingInitialData}
      />
    ),
    [
      fetchMoreSalesOrders,
      hasMoreSalesOrders,
      loadingInitialData,
      onSalesOrderSelect,
      salesOrders,
      selectedSalesOrderId,
    ]
  );

  const table = useMemo(() => {
    if (loadingInitialSoftware) {
      return <PageSpinner />;
    }
    if (selectedSalesOrderId) {
      return (
        <SoftwareAssignmentTable
          dataSource={software}
          config={tableConfig}
          onChange={onTableChange}
          includeSalesOrderColumn={selectedSalesOrderId === SelectionFilter.ALL}
          empty={
            <Element
              display="flex"
              justifyContent="center"
              alignItems="center"
              className="h-full"
            >
              <span className="has-text-weight-light">{tableEmptyText}</span>
            </Element>
          }
          form={{
            enableSearch: false,
            leftExtra: (
              <SelectionIndicator
                selected={activeSalesOrderSelectedSoftwareCount}
                total={activeSalesOrderTotalSoftwareCount}
              />
            ),
          }}
          onSoftwareTitleClick={(software, add) =>
            onSoftwareTitleClick(selectedSalesOrderId, software, add)
          }
          selectedSoftware={selectedSoftware}
          showHeaderDivider
          bordered={false}
        />
      );
    }
    return (
      <DefaultInboxTable text="Please select a sales order to display software" />
    );
  }, [
    activeSalesOrderSelectedSoftwareCount,
    activeSalesOrderTotalSoftwareCount,
    loadingInitialSoftware,
    onSoftwareTitleClick,
    onTableChange,
    selectedSalesOrderId,
    selectedSoftware,
    software,
    tableConfig,
    tableEmptyText,
  ]);

  return (
    <InboxTable
      inbox={inbox}
      table={table}
      config={tableConfig}
      onSearchChange={onSearchChange}
    />
  );
}

export default SoftwareAssignmentInboxTable;
